import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/styles';

/**
 * Selectors used to locate elements. Deliberately attribute/type based —
 * no classes or ids, so this stays decoupled from styling.
 */
const selectors = {
    input: 'input[type="tel"]',
};

/**
 * defaults for this project
 *
 * Note: the intl-tel-input option names changed between major versions.
 * see https://intl-tel-input.com/docs/options for the current list.
 */
const defaultOptions = {
    initialCountryLookup: async () => {
        try {
            const res = await fetch('https://ipapi.co/json');
            const data = await res.json();
            return data.country_code.toLowerCase();
        } catch {
            return 'nl'; // fallback
        }
    },
    separateDialCode: true,
    loadUtils: () => import('intl-tel-input/utils'),
};

/**
 * Initialise intl-tel-input on the `input[type="tel"]` found inside the
 * given field container.
 *
 * @param {string} fieldSelector - selector for the field container, e.g.
 *   `[data-form-field="phone"]`
 * @param {object} [options] - overrides merged over defaultOptions, e.g.
 *   this.options?.phoneValidation from application-form.js
 * @returns {import('intl-tel-input').Iti|null} the intl-tel-input instance,
 *   or null if the container/input couldn't be found
 */
export function initPhoneInput(fieldSelector, options = {}) {
    const container = document.querySelector(fieldSelector);

    if (!container) {
        console.error(
            `intl-tel-input: no element found for selector "${fieldSelector}"`
        );
        return null;
    }

    const input = container.querySelector(selectors.input);

    if (!input) {
        console.error(
            'intl-tel-input: no tel input found inside',
            fieldSelector
        );
        return null;
    }

    const itiOptions = {
        ...defaultOptions,
        ...options,
    };

    // If specific countries were configured, restrict the dropdown to them.
    // If no countries were configured, show all countries by default.
    if (Array.isArray(options.countries) && options.countries.length > 0) {
        itiOptions.onlyCountries = options.countries
            .map(country => String(country).trim().toLowerCase())
            .filter(Boolean);
    }

    delete itiOptions.countries;

    return intlTelInput(input, itiOptions);
}

/**
 * Get the intl-tel-input instance attached to a given tel input element.
 * Uses the imported module directly, so it does not depend on
 * `window.intlTelInput` (which only exists in the script-tag/UMD build).
 *
 * @param {HTMLInputElement} input
 * @returns {import('intl-tel-input').Iti|null}
 */
export function getInstanceForInput(input) {
    return input ? intlTelInput.getInstance(input) : null;
}

/**
 * Get the existing intl-tel-input instance for a phone field.
 * The phone input must have already been initialised with initPhoneInput().
 *
 * @param {string} fieldSelector
 * @returns {import('intl-tel-input').Iti|null}
 */
export function getPhoneInputInstance(fieldSelector) {
    const container = document.querySelector(fieldSelector);
    const input = container?.querySelector(selectors.input);
    return getInstanceForInput(input);
}

/**
 * Get the ISO2 country code currently selected in the phone input,
 * uppercased for use with postal code validation.
 *
 * @param {string} [fieldSelector] - selector for the phone field container
 * @param {string} [fallback] - country code to use if no selection is found
 * @returns {string}
 */
export function getSelectedCountryCode(fieldSelector = '[data-form-field="phone"]', fallback = 'NL') {
    const iti = getPhoneInputInstance(fieldSelector);
    return iti?.getSelectedCountry()?.iso2?.toUpperCase() || fallback;
}

/**
 * Get the full phone number in E.164 format (e.g. "+31612345678") from an
 * intl-tel-input instance, i.e. {countryCode}{enteredNumber}.
 *
 * Waits for `iti.promise` (utils + auto-country detection) to settle first.
 * If the user started typing before auto-detection resolved, intl-tel-input
 * never applies the detected country to the field (to avoid changing it
 * under the user), so we apply it ourselves as a last resort before reading
 * the number.
 *
 * @param {import('intl-tel-input').Iti} iti
 * @returns {Promise<string>} the E.164 number, or '' if empty/invalid
 */
export async function getE164(iti) {
    await iti.promise;

    if (!iti.getSelectedCountry() && intlTelInput.autoCountry) {
        iti.setSelectedCountry(intlTelInput.autoCountry);
    }

    return iti.getNumber();
}